import type { AxiosError } from 'axios'
import { apiClient } from './client'

/**
 * Звонки на уроке (WebRTC). Сервер отвечает за права, историю и временные
 * креды TURN; аудио/видео идёт между браузерами напрямую (или через relay).
 * Сигнализация SDP/ICE — client events в presence-канале `lesson.{id}.call`
 * (см. `@/webrtc/callSignaling`).
 */

export type LessonCallType = 'audio' | 'video'

export type LessonCallEndReason = 'completed' | 'dropped' | 'rejected' | 'timeout'

export type LessonCallParticipant = {
  id: number
  user_id: number
  user_name?: string
  joined_at: string | null
  left_at: string | null
  is_active: boolean
}

export type LessonCallSession = {
  id: number
  lesson_id: number
  initiator_user_id: number
  type: LessonCallType
  started_at: string | null
  ended_at: string | null
  end_reason: LessonCallEndReason | null
  is_active: boolean
  participants?: LessonCallParticipant[]
}

export type LessonCallQualityStats = {
  packet_loss?: number
  jitter_ms?: number
  rtt_ms?: number
  relay_share?: number
  disconnects?: number
}

export type WebrtcIceServer = {
  urls: string[]
  username?: string
  credential?: string
}

export type WebrtcVideoConstraints = {
  width: number
  height: number
  frame_rate: number
  max_bitrate: number
}

export type WebrtcConfig = {
  enabled: boolean
  ice_servers: WebrtcIceServer[]
  turn_expires_at: string | null
  audio: { enabled: boolean; max_participants: number }
  video: {
    enabled: boolean
    max_participants: number
    quality: 'sd' | 'hd'
    constraints: WebrtcVideoConstraints
  }
  screen_share_enabled: boolean
  heartbeat_interval_seconds: number
}

type ApiResponse<T> = { data?: T; message?: string }

/**
 * Пробрасывает ошибку с кодом подписки, чтобы UI показал CTA «Перейти к
 * подписке» вместо общего текста ошибки (тот же паттерн, что в dictionaryFill).
 */
export type LessonCallApiError = Error & {
  status?: number
  code?: string
  parameter?: string
}

/**
 * Тело ответа обязательно: без сессии или конфига вызывающий код всё равно
 * упадёт дальше, но уже в непонятном месте — лучше явная ошибка здесь.
 */
function unwrapSession(payload: ApiResponse<LessonCallSession>): LessonCallSession {
  if (!payload?.data) {
    throw new Error('Пустой ответ API звонка')
  }
  return payload.data
}

function unwrapConfig(payload: ApiResponse<WebrtcConfig>): WebrtcConfig {
  if (!payload?.data) {
    throw new Error('Пустой ответ webrtc-config')
  }
  return payload.data
}

function rethrowRich(err: unknown): never {
  const axiosErr = err as AxiosError<{ message?: string; error?: string; parameter?: string }>
  const data = axiosErr.response?.data
  const rich = new Error(data?.message ?? '') as LessonCallApiError
  if (axiosErr.response?.status !== undefined) rich.status = axiosErr.response.status
  if (data?.error) rich.code = data.error
  if (data?.parameter) rich.parameter = data.parameter
  throw rich
}

export const lessonCallsApi = {
  /** Активная сессия звонка урока (null — звонка нет). */
  getActive: async (lessonId: number): Promise<LessonCallSession | null> => {
    const response = await apiClient.get<ApiResponse<LessonCallSession | null>>(
      `/lessons/${lessonId}/call`,
    )
    return response.data?.data ?? null
  },

  getWebrtcConfig: async (lessonId: number): Promise<WebrtcConfig> => {
    const response = await apiClient.get<ApiResponse<WebrtcConfig>>(
      `/lessons/${lessonId}/call/webrtc-config`,
    )
    return unwrapConfig(response.data)
  },

  start: async (lessonId: number, type: LessonCallType = 'audio'): Promise<LessonCallSession> => {
    try {
      const response = await apiClient.post<ApiResponse<LessonCallSession>>(
        `/lessons/${lessonId}/call/start`,
        { type },
      )
      return unwrapSession(response.data)
    } catch (err) {
      rethrowRich(err)
    }
  },

  join: async (lessonId: number, sessionId: number): Promise<LessonCallSession> => {
    try {
      const response = await apiClient.post<ApiResponse<LessonCallSession>>(
        `/lessons/${lessonId}/call/${sessionId}/join`,
      )
      return unwrapSession(response.data)
    } catch (err) {
      rethrowRich(err)
    }
  },

  leave: async (
    lessonId: number,
    sessionId: number,
    qualityStats?: LessonCallQualityStats,
  ): Promise<LessonCallSession> => {
    const response = await apiClient.post<ApiResponse<LessonCallSession>>(
      `/lessons/${lessonId}/call/${sessionId}/leave`,
      qualityStats ? { quality_stats: qualityStats } : {},
    )
    return unwrapSession(response.data)
  },

  /** Отклонить входящий: на индивидуальном уроке закрывает звонок как rejected. */
  reject: async (lessonId: number, sessionId: number): Promise<LessonCallSession> => {
    const response = await apiClient.post<ApiResponse<LessonCallSession>>(
      `/lessons/${lessonId}/call/${sessionId}/reject`,
    )
    return unwrapSession(response.data)
  },

  end: async (lessonId: number, sessionId: number): Promise<LessonCallSession> => {
    const response = await apiClient.post<ApiResponse<LessonCallSession>>(
      `/lessons/${lessonId}/call/${sessionId}/end`,
    )
    return unwrapSession(response.data)
  },

  heartbeat: async (lessonId: number, sessionId: number): Promise<void> => {
    await apiClient.post(`/lessons/${lessonId}/call/${sessionId}/heartbeat`)
  },
}
